import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Map, Shield, LogOut, User, Crown, LayoutDashboard, Sparkles, Building2, Menu, X } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useConfig } from "../lib/ConfigContext";
import { Button } from "../components/ui/button";
import { LOGO_URL, BRAND_SHORT, BRAND_TAGLINE } from "../lib/brand";
import { useBrand } from "../lib/BrandContext";
import AccessHouseEntryCTA from "../components/AccessHouseEntryCTA";
import { AdminCommandPaletteTrigger } from "../shared/components/AdminCommandPalette";
import NotificationBell from "../components/NotificationBell";

const TIER_BADGE = {
  normal: { label: "Normal", cls: "bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]" },
  prime: { label: "Prime", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
  premium: { label: "Premium", cls: "bg-[#0055FF] text-white border-[#0055FF]" },
};

export default function Header() {
  const { pathname } = useLocation();
  const { user, isAdmin, isDealer, isBuilder, isClient, isTeamMember, tier, logout, loading } = useAuth();
  const { settings } = useConfig();
  const freeMode = !!settings?.free_mode;
  const plansEnabled = settings?.plans_enabled !== false;
  const { logoUrl } = useBrand();
  const headerLogo = logoUrl || LOGO_URL;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pages, setPages] = useState([]);

  // Fetch public CMS pages for footer links (lightweight; ignore errors)
  React.useEffect(() => {
    let ok = true;
    import("../lib/api").then((m) => m.default.get("/pages").then((r) => { if (ok) setPages(r.data || []); }).catch(() => {}));
    return () => { ok = false; };
  }, []);

  // Role-aware dashboard route
  const dashboardRoute = isAdmin ? "/admin" : (isDealer || isBuilder) ? (isBuilder ? "/builder" : "/dealer") : isTeamMember ? "/team" : null;

  // Team console + auth pages + plans-preview have their own minimal headers — hide site header
  // v2.65 — Path-based hide ALWAYS applies (pathname is sync, no flicker).
  // BUT team-member detection requires auth to resolve, so defer it until
  // `loading === false`. Otherwise the first paint (while /auth/me is in flight)
  // briefly renders the header for a team member, then unmounts it once auth
  // resolves — causing a visible flicker on every refresh/cold preview load.
  const AUTH_ROUTES = ["/team", "/team-login", "/access", "/login", "/register", "/forgot-password", "/magic", "/reset-password", "/plans-preview", "/office"];
  const onAuthRoute = AUTH_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));
  if (onAuthRoute || (!loading && isTeamMember)) {
    return null;
  }

  const linkCls = (path) =>
    `nav-link-underline text-[13.5px] font-semibold tracking-tight transition-colors ${
      pathname === path ? "text-[#09090B]" : "text-[#52525B] hover:text-[#09090B]"
    }`;
  const tierInfo = tier ? TIER_BADGE[tier] : null;

  return (
    <header className="sticky top-0 z-50 glass-nav transition-all duration-300 site-header-sticky shadow-[inset_0_-1px_0_rgba(228,228,231,0.6)]" data-testid="site-header">
      {/* v2.87.x — Compact heights (was 56/64/68 → now 48/52/56). The
          glass-nav background already dissolves into the hero on scroll
          via backdrop-blur; the new heights reduce sticky-UI weight by
          ~17% on desktop and free vertical real estate on mobile. */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 h-[48px] sm:h-[52px] lg:h-[56px] flex items-center justify-between gap-2 min-w-0">
        <Link to="/" className="flex items-center gap-2.5 sm:gap-3 group min-w-0 flex-shrink" data-testid="logo-link">
          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#09090B] flex items-center justify-center rounded-sm overflow-hidden flex-shrink-0" data-testid="brand-logo-wrap">
            <img src={headerLogo} alt={BRAND_SHORT} className="w-full h-full object-contain" data-testid="brand-logo-img" loading="eager" decoding="async" />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-heading font-bold text-[14px] sm:text-[15px] tracking-tight text-[#09090B] truncate">
              House<span className="text-[#E53935]">Entry</span> <span className="text-[#52525B] font-medium">Solutions</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#A1A1AA] font-semibold hidden lg:block truncate">
              {BRAND_TAGLINE}
            </div>
          </div>
        </Link>

        {/* v2.71.16 — Desktop nav now at `lg:flex` (was `md:flex`).
            At md (768-1023px) the nav was overflowing the viewport horizontally
            (7 items × ~70px gap-7 plus right-side action buttons > 768px). */}
        <nav className="hidden lg:flex items-center gap-7 flex-shrink-0">
          <Link to="/" className={linkCls("/")} data-active={pathname === "/"} data-testid="nav-dashboard">Listings</Link>
          <Link to="/projects" className={linkCls("/projects")} data-active={pathname === "/projects"} data-testid="nav-projects">
            <span className="inline-flex items-center gap-1.5"><Building2 size={14} /> Projects</span>
          </Link>
          <Link to="/map" className={linkCls("/map")} data-active={pathname === "/map"} data-testid="nav-map">
            <span className="inline-flex items-center gap-1.5"><Map size={14} /> Map</span>
          </Link>
          {/* Plans link is login-gated, role-aware AND honors `plans_enabled` toggle */}
          {user && (isAdmin || isClient) && plansEnabled && (
            <Link to="/pricing" className={linkCls("/pricing")} data-active={pathname === "/pricing"} data-testid="nav-pricing">
              <span className="inline-flex items-center gap-1.5"><Sparkles size={14} /> Plans</span>
            </Link>
          )}
          {isDealer && !freeMode && plansEnabled && (
            <Link to="/dealer?tab=plan" className={linkCls("/dealer")} data-testid="nav-dealer-plan">
              <span className="inline-flex items-center gap-1.5"><Sparkles size={14} /> My Plan</span>
            </Link>
          )}
          {isDealer && (
            <Link to="/dealer" className={linkCls("/dealer")} data-active={pathname === "/dealer"} data-testid="nav-dealer">
              <span className="inline-flex items-center gap-1.5"><LayoutDashboard size={14} /> My Submissions</span>
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Mobile menu trigger — visible below `lg` (was below `md`).
              Tablets (md…lg) now use the drawer too. */}
          <button
            type="button"
            className="lg:hidden p-2 -mr-1 rounded-sm border border-[#E4E4E7] hover:border-[#09090B]"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
          {!user ? (
            <>
              <AccessHouseEntryCTA variant="header" data-testid="nav-register" />
            </>
          ) : (
            <>
              {/* v2.71.66 — Admin Cmd+K palette trigger. Renders only for admins. */}
              {isAdmin && <AdminCommandPaletteTrigger />}
              {/* v2.71.96 — Notification bell — visible for client/dealer/builder/admin */}
              <NotificationBell variant="light" />
              {tierInfo && (isAdmin || isClient) && (
                <span className={`hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border rounded-sm ${tierInfo.cls}`} data-testid="header-tier-badge">
                  {tier === "premium" && <Crown size={11} />} {tierInfo.label}
                </span>
              )}
              {dashboardRoute && (
                <Link to={dashboardRoute} data-testid="nav-dashboard-btn">
                  <Button size="sm" className="btn-brand rounded-sm">
                    {isAdmin ? <Shield size={14} className="mr-1.5" /> : <LayoutDashboard size={14} className="mr-1.5" />}
                    {isAdmin ? "Admin" : "Dashboard"}
                  </Button>
                </Link>
              )}
              {isClient && !tier && (
                <Link to="/pricing" data-testid="upgrade-cta">
                  <Button size="sm" className="btn-brand rounded-sm">Upgrade</Button>
                </Link>
              )}
              <Link
                to="/profile"
                className="hidden xl:inline-flex items-center gap-1.5 text-[12px] text-[#52525B] hover:text-[#0055FF] hover:bg-[#EEF4FF] px-2 py-1.5 rounded-sm transition-colors max-w-[140px] truncate"
                data-testid="nav-profile"
                title="My Profile"
              >
                <User size={13} /> {user.name || user.email.split("@")[0]}
              </Link>
              <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn" className="rounded-sm">
                <LogOut size={14} />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Mobile drawer — shown below `lg` (was below `md`) for symmetry with
          the hamburger trigger. Keeps tablet (md…lg) on the drawer experience
          since the desktop nav was overflowing horizontally at md widths. */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[#E4E4E7] bg-white" data-testid="mobile-drawer">
          <nav className="flex flex-col py-2 px-6 space-y-1">
            <Link to="/" className="py-2.5 text-[14px] font-semibold text-[#09090B] hover:text-[#0055FF]" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-listings">Listings</Link>
            <Link to="/projects" className="py-2.5 text-[14px] font-semibold text-[#09090B] hover:text-[#0055FF] inline-flex items-center gap-1.5" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-projects"><Building2 size={14} /> Projects</Link>
            <Link to="/map" className="py-2.5 text-[14px] font-semibold text-[#09090B] hover:text-[#0055FF] inline-flex items-center gap-1.5" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-map"><Map size={14} /> Map</Link>
            {user && (isAdmin || isClient) && plansEnabled && (
              <Link to="/pricing" className="py-2.5 text-[14px] font-semibold text-[#09090B] hover:text-[#0055FF] inline-flex items-center gap-1.5" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-pricing"><Sparkles size={14} /> Plans</Link>
            )}
            {isDealer && (
              <>
                {!freeMode && plansEnabled && (
                  <Link to="/dealer?tab=plan" className="py-2.5 text-[14px] font-semibold text-[#09090B] hover:text-[#0055FF] inline-flex items-center gap-1.5" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-dealer-plan"><Sparkles size={14} /> My Plan</Link>
                )}
                <Link to="/dealer" className="py-2.5 text-[14px] font-semibold text-[#09090B] hover:text-[#0055FF] inline-flex items-center gap-1.5" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-dealer"><LayoutDashboard size={14} /> My Submissions</Link>
              </>
            )}
            {dashboardRoute && (
              <Link to={dashboardRoute} className="py-2.5 text-[14px] font-semibold text-[#09090B] hover:text-[#0055FF] inline-flex items-center gap-1.5" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-dashboard">
                {isAdmin ? <Shield size={14} /> : <LayoutDashboard size={14} />}
                {isAdmin ? "Admin Dashboard" : "Dashboard"}
              </Link>
            )}
            {user && (
              <Link to="/profile" className="py-2.5 text-[14px] font-semibold text-[#09090B] hover:text-[#0055FF] inline-flex items-center gap-1.5" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-profile">
                <User size={14} /> My Profile
              </Link>
            )}
            {/* CMS pages — bottom of mobile drawer */}
            {pages.length > 0 && (
              <div className="pt-2 mt-1 border-t border-[#E4E4E7]">
                {pages.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/page/${p.slug}`}
                    className="block py-2 text-[13px] text-[#52525B] hover:text-[#0055FF]"
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-page-${p.slug}`}
                  >{p.title || p.slug}</Link>
                ))}
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
